import ComplaintScopeFilters, { appendOriginParams } from '../../components/ComplaintScopeFilters';
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { OverlayPanel } from 'primereact/overlaypanel';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { endPoints } from '../../Config';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { toast } from 'react-toastify';
import PhotoUploader from '../../components/PhotoUploader';
import { formatTimeFromMinutes } from '../../utilities/formatTime';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';
import {
    getAdminAutomationStatus,
    getAutomationPresentation,
    getComplaintAutomationState,
    getSafeAdminTogglePayload
} from '../../utilities/complaintAutomationUi';
import { withOpenComplaintSortFields } from './openComplaintSorting';

const OPEN_COMPLAINTS_QUERY_STORAGE_KEY_PREFIX = 'open-complaints-list-query';
const normalizeSearchValue = (value) => String(value || '').trim();
const normalizeDigits = (value) => normalizeSearchValue(value).replace(/\D/g, '');
const formatFilterDate = (value) => {
    if (!value) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const parseFilterDate = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (
        Number.isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) return null;
    return date;
};
const parseNonNegativeInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};
const startOfDay = (value) => {
    if (!value) return null;
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
};
const endOfDay = (value) => {
    if (!value) return null;
    const date = new Date(value);
    date.setHours(23, 59, 59, 999);
    return date;
};
const getLatestTechnicalHandoff = (complaint) => {
    const handoffs = Array.isArray(complaint?.technicalHandoffs) ? complaint.technicalHandoffs : [];
    return handoffs[handoffs.length - 1] || null;
};

const buildComplaintSearchRow = (complaint) => {
    const machine = complaint?.machine || {};
    const user = complaint?.user || {};
    const machineUuid = normalizeSearchValue(machine?.uuid);
    const machineSerialNumber = normalizeSearchValue(machine?.serialNumber);
    const machineDeviceId = normalizeSearchValue(machine?.deviceId);
    const machineExternalSystemId = normalizeSearchValue(machine?.externalSystemId);
    const machineLocation = normalizeSearchValue(machine?.location);
    const machineRoute = normalizeSearchValue(machine?.route);
    const machineName = normalizeSearchValue(machine?.machine);
    const machineType = normalizeSearchValue(machine?.type);
    const machineModel = normalizeSearchValue(machine?.model || machine?.makeModel);
    const whatsAppNumber = normalizeSearchValue(complaint?.whatsAppNumber);
    const whatsAppDigits = normalizeDigits(complaint?.whatsAppNumber);
    const userDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const statusLabel = normalizeSearchValue(complaint?.status);
    const refundBankName = normalizeSearchValue(complaint?.refund?.bankName);
    const handoff = getLatestTechnicalHandoff(complaint);
    const handoffSearch = [
        handoff?.reason,
        handoff?.sentBy?.firstName,
        handoff?.sentBy?.lastName,
        handoff?.repairOrder?.technician?.firstName,
        handoff?.repairOrder?.technician?.lastName,
        handoff?.technician?.firstName,
        handoff?.technician?.lastName
    ].filter(Boolean).join(' ');
    const searchableText = [
        machineUuid,
        machineSerialNumber,
        machineDeviceId,
        machineExternalSystemId,
        machineLocation,
        machineRoute,
        machineName,
        machineType,
        machineModel,
        whatsAppNumber,
        whatsAppDigits,
        userDisplayName,
        statusLabel,
        refundBankName,
        handoffSearch
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return {
        ...complaint,
        automationState: getComplaintAutomationState(complaint),
        machineUuid,
        machineSerialNumber,
        machineDeviceId,
        machineExternalSystemId,
        machineLocation,
        machineRoute,
        machineName,
        machineType,
        machineModel,
        whatsAppNumber,
        whatsAppDigits,
        userDisplayName,
        statusLabel,
        refundBankName,
        searchableText,
        machineSearchDisplay:
            machineUuid ||
            machineSerialNumber ||
            machineDeviceId ||
            machineExternalSystemId ||
            '-'
    };
};

const OpenComplaints = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [complaintList, setComplaintList] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const globalFilter = searchParams.get('q') || '';
    const startDate = parseFilterDate(searchParams.get('from'));
    const endDate = parseFilterDate(searchParams.get('to'));
    // Keep accepting the dashboard's existing `status` link while storing list filters
    // under the more explicit `ticketStatus` key.
    const origin = searchParams.get('origin') || '';
    const selectedTeam = searchParams.get('teamId') || '';
    const includeInternal = searchParams.get('includeInternal') === '1';
    const statusFilter = searchParams.get('ticketStatus') || searchParams.get('status') || null;
    const onlyNeedsReply = searchParams.get('reply') === '1';
    const automationFilter = searchParams.get('automation') || null;
    const technicalStageFilter = searchParams.get('techStage') || null;
    const technicianFilter = searchParams.get('technician') || null;
    const tableFirst = parseNonNegativeInteger(searchParams.get('first'), 0);
    const requestedTableRows = parseNonNegativeInteger(searchParams.get('rows'), 15);
    const tableRows = [5, 10, 15, 20].includes(requestedTableRows) ? requestedTableRows : 15;
    const [showManualModal, setShowManualModal] = useState(false);
    const [machineOptions, setMachineOptions] = useState([]);
    const [savingManual, setSavingManual] = useState(false);
    const [automationSettings, setAutomationSettings] = useState(null);
    const [automationToggleLoading, setAutomationToggleLoading] = useState(false);
    const [activeView, setActiveView] = useState('pending');
    const [historyScope, setHistoryScope] = useState('mine');
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const filterPanelRef = useRef(null);
    const listRequest = useRef(0);
    const [manualForm, setManualForm] = useState({
        machineId: '',
        issueDetails: '',
        photoUrl: null,
        complaintType: 'refund',
        contactChannel: 'whatsapp',
        customerName: '',
        customerPhone: '',
        manualNotes: ''
    });

    const user = JSON.parse(localStorage.getItem('userDetails'));
    const adminRole = user?.data?.adminUser?.role;
    const adminRoleUid = typeof adminRole === 'string' ? adminRole : adminRole?.uid;
    const isAutomationAdmin = ['admin', 'superAdmin'].includes(adminRoleUid);
    const openComplaintsQueryStorageKey =
        `${OPEN_COMPLAINTS_QUERY_STORAGE_KEY_PREFIX}:${user?.data?.adminUser?._id || 'anonymous'}`;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const currentQuery = searchParams.toString();
        if (currentQuery) {
            sessionStorage.setItem(openComplaintsQueryStorageKey, currentQuery);
            return;
        }
        const storedQuery = sessionStorage.getItem(openComplaintsQueryStorageKey);
        if (storedQuery) {
            setSearchParams(new URLSearchParams(storedQuery), { replace: true });
        }
        // Restore the last list state only when this screen is mounted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateListQuery = (updates, { resetPage = true } = {}) => {
        const nextParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                nextParams.delete(key);
            } else {
                nextParams.set(key, String(value));
            }
        });
        if (resetPage) nextParams.delete('first');
        const nextQuery = nextParams.toString();
        if (nextQuery) sessionStorage.setItem(openComplaintsQueryStorageKey, nextQuery);
        else sessionStorage.removeItem(openComplaintsQueryStorageKey);
        setSearchParams(nextParams, { replace: true });
    };

    const handleNavigateToChat = (rowData) => {
        navigate('/complaints/chat', {
            state: {
                complaint: rowData,
                returnTo: `${location.pathname}${location.search}`
            }
        });
    };

    const chatBodyTemplate = (rowData) => {
        return (
            <Button
                label={t('Open Chat')}
                icon="pi pi-comments"
                className="p-button-rounded p-button-sm"
                tooltip={t('Open chat with this complain')}
                tooltipOptions={{ position: 'top' }}
                style={{ backgroundColor: '#2563EB', border: 'none' }}
                onClick={() => handleNavigateToChat(rowData)}
            />
        );
    };

    const getComplaintList = async (view = activeView, scope = historyScope) => {
        const request = ++listRequest.current;
        const adminUser = user?.data?.adminUser;
        const role = adminUser?.role || {};
        const roleUid = role?.uid;
        const teamId = adminUser?.teamId;

        const isSupport = roleUid === 'support';

        const params = new URLSearchParams();
        // This page is Customer Support's complaint queue. Repair orders created
        // directly in Operations keep their backing complaint, but belong only in
        // the Repair Orders module.
        params.append('customerSupportQueue', includeInternal ? '0' : '1');
        appendOriginParams(params, origin);
        if (!isSupport && selectedTeam) params.set('teamId', selectedTeam);
        if (view === 'sent') {
            params.append('technicalHandoff', '1');
            if (scope === 'mine' && adminUser?._id) params.append('sentBy', adminUser._id);
        } else {
            params.append('status', 'open');
            if (isSupport && teamId) params.append('teamId', teamId);
        }

        const response = await handleGetRequest(endPoints.getallComplaints(params.toString()), true, false, dispatch);
        if (request !== listRequest.current) return;
        const searchableComplaints = (response?.data || []).map(buildComplaintSearchRow);
        setComplaintList(searchableComplaints);
    };

    const getTechnicianOptions = async () => {
        const response = await handleGetRequest(endPoints.getTechnicians, false, false, dispatch);
        const list = Array.isArray(response?.data) ? response.data : [];
        setTechnicianOptions(list.map((technician) => ({
            label: [technician?.firstName, technician?.lastName].filter(Boolean).join(' ') || technician?.email,
            value: technician?._id
        })).filter((option) => option.value));
    };


    const getMachineOptions = async () => {
        const response = await handleGetRequest(endPoints.getallMachine, false, false, dispatch);
        const list = Array.isArray(response?.data) ? response.data : [];
        const options = list
            .filter((machine) => !machine?.isDeleted)
            .map((machine) => {
                const uuid = normalizeSearchValue(machine?.uuid);
                const serial = normalizeSearchValue(machine?.serialNumber);
                const route = normalizeSearchValue(machine?.route);
                const machineName = normalizeSearchValue(machine?.machine);
                const type = normalizeSearchValue(machine?.type);
                const typeLabel = {
                    drink: 'Soda / bebidas',
                    snack: 'Snack',
                    coffee: 'Café',
                    combo: 'Combo',
                    others: 'Otros'
                }[type.toLowerCase()] || type || 'Sin tipo registrado';
                const labelParts = [uuid || serial || machine?._id, typeLabel, route, machineName].filter(Boolean);
                return {
                    label: labelParts.join(' • '),
                    typeLabel,
                    value: machine?._id
                };
            });
        setMachineOptions(options);
    };

    const getAutomationSettings = async () => {
        if (!isAutomationAdmin) return;
        const response = await handleGetRequest(
            endPoints.complaintAutomationSettings,
            false,
            false,
            dispatch
        );
        if (response?.data) setAutomationSettings(response.data);
    };

    const toggleAutomation = async () => {
        if (!isAutomationAdmin || !automationSettings || automationToggleLoading) return;
        const payload = getSafeAdminTogglePayload(automationSettings);
        if (payload.enabled && !automationSettings.environmentEnabled) {
            toast.warn(t('Server enablement is required before the agent can be activated.'));
            return;
        }
        setAutomationToggleLoading(true);
        try {
            const response = await dispatch(
                handlePatchRequest(payload, endPoints.toggleComplaintAutomation, false, false)
            );
            if (response?.status === 200 && response?.data?.data) {
                setAutomationSettings(response.data.data);
                toast.success(
                    t(payload.enabled ? 'Agent shadow evaluation started. No customer messages will be sent.' : 'Agent turned off.'),
                    { autoClose: 1500 }
                );
            }
        } finally {
            setAutomationToggleLoading(false);
        }
    };

    const requestAutomationToggle = () => {
        if (!automationSettings) return;
        const payload = getSafeAdminTogglePayload(automationSettings);
        confirmDialog({
            header: t(payload.enabled ? 'Activate ticket agent?' : 'Turn off ticket agent?'),
            message: t(
                payload.enabled
                    ? 'This starts shadow evaluation for eligible tickets. Decisions are audited, but no customer messages are sent.'
                    : 'This stops new analysis and activates the kill switch.'
            ),
            icon: payload.enabled ? 'pi pi-info-circle' : 'pi pi-exclamation-triangle',
            acceptLabel: t(payload.enabled ? 'Start shadow evaluation' : 'Turn agent off'),
            rejectLabel: t('Cancel'),
            acceptClassName: payload.enabled ? 'p-button-info' : 'p-button-danger',
            accept: toggleAutomation
        });
    };

    useEffect(() => {
        getMachineOptions();
        getTechnicianOptions();
        getAutomationSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getComplaintList(activeView, historyScope);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView, historyScope, origin, selectedTeam, includeInternal]);

    const resetManualForm = () => {
        setManualForm({
            machineId: '',
            issueDetails: '',
            photoUrl: null,
            complaintType: 'refund',
            contactChannel: 'whatsapp',
            customerName: '',
            customerPhone: '',
            manualNotes: ''
        });
    };

    const submitManualComplaint = async () => {
        if (!manualForm.machineId || !manualForm.issueDetails.trim()) return;
        setSavingManual(true);
        const payload = {
            machineId: manualForm.machineId,
            issueDetails: manualForm.issueDetails.trim(),
            photoUrl: manualForm.photoUrl?.trim() || null,
            source: 'manual_portal',
            complaintType: manualForm.complaintType || 'refund',
            contactChannel: manualForm.contactChannel || 'whatsapp',
            customerName: manualForm.customerName?.trim() || null,
            customerPhone: manualForm.customerPhone?.trim() || null,
            manualNotes: manualForm.manualNotes?.trim() || null
        };
        const response = await dispatch(handlePostRequest(payload, endPoints.createManualComplaint, true));
        setSavingManual(false);
        if (response?.status === 201) {
            setShowManualModal(false);
            resetManualForm();
            await getComplaintList();
        }
    };

    const statusBody = (rowData) => {
        const rawStatus = (rowData?.status || '').toLowerCase();
        const mapLabel = {
            open: t('Open'),
            in_progress: t('Finance'),
            awaiting_customer: t('Awaiting Customer'),
            close: t('Closed'),
            forwarded: t('Forwarded'),
            pending: t('Pending')
        };
        const mapSeverity = {
            open: 'success',
            in_progress: 'info',
            awaiting_customer: 'warning',
            close: 'danger',
            forwarded: 'info',
            pending: 'warning'
        };
        const label = mapLabel[rawStatus] || rawStatus;
        const severity = mapSeverity[rawStatus] || 'info';

        // The badge was being repainted by ticket AGE, so "Abierto" showed red on a ticket
        // nobody was waiting on. Status shows status; urgency lives in the Waiting column.
        return (
            <div className="flex justify-content-center">
                <Tag
                    value={label}
                    severity={severity}
                    className="px-3 py-1 text-xs border-round-lg"
                />
            </div>
        );
    };
    const createdDateAndTimeBodyTemplate = (rowData) => {
        const isoDate = rowData?.createdAt;
        const date = new Date(isoDate);

        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yyyy = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');

        let ampm = 'AM';
        if (hours >= 12) {
            ampm = 'PM';
            hours -= 12;
        }
        if (hours === 0) {
            hours = 12;
        }

        const formattedDateTime = `${dd}/${mm}/${yyyy} ${hours}:${minutes} ${ampm}`;
        return <p>{formattedDateTime}</p>;
    };

    const minutesSince = (value) => {
        if (!value) return null;
        return Math.floor((Date.now() - new Date(value).getTime()) / 60000);
    };

    // How long the CUSTOMER has been waiting -- null when the ball is not in our court.
    const waitingMinutes = (rowData) => {
        if (!rowData?._signals?.needsAgentReply) return null;
        return minutesSince(rowData._signals.lastCustomerMessageAt || rowData.createdAt);
    };

    // Colour by waiting time, not by ticket age. Age coloured 53 of 73 live rows red or
    // orange while nobody was waiting on us -- an alarm that is wrong most of the time
    // teaches people to ignore it. Tickets where the customer is not waiting stay grey.
    const getWaitColor = (minutes) => {
        if (minutes === null) return '#94a3b8';
        if (minutes < 120) return '#22c55e';
        if (minutes < 480) return '#eab308';
        if (minutes < 1440) return '#f97316';
        return '#ef4444';
    };

    const waitingBodyTemplate = (rowData) => {
        const minutes = waitingMinutes(rowData);
        if (minutes === null) {
            return <span style={{ color: '#94a3b8' }}>{t('Not waiting on us')}</span>;
        }
        return (
            <span style={{ color: getWaitColor(minutes), fontWeight: 'bold' }}>
                {formatTimeFromMinutes(minutes)}
            </span>
        );
    };

    const ageBodyTemplate = (rowData) => {
        const minutes = minutesSince(rowData?.createdAt);
        if (minutes === null) return '-';
        return <span style={{ color: '#64748b' }}>{formatTimeFromMinutes(minutes)}</span>;
    };

    const originBodyTemplate = (rowData) =>
        rowData?._signals?.raisedBy === 'staff' ? (
            <span
                title={t('Reported by one of our operators, not a customer')}
                style={{
                    background: '#ecfdf5',
                    color: '#047857',
                    borderRadius: '10px',
                    padding: '0.15rem 0.5rem',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap'
                }}
            >
                <i className="pi pi-wrench mr-1" style={{ fontSize: '0.7rem' }} />
                {t('Operator')}
            </span>
        ) : null;

    const draftBodyTemplate = (rowData) =>
        rowData?._signals?.hasFreshDraft ? (
            <span
                title={t('An AI suggestion is ready for this ticket')}
                style={{
                    background: '#eef2ff',
                    color: '#4338ca',
                    borderRadius: '10px',
                    padding: '0.15rem 0.5rem',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap'
                }}
            >
                <i className="pi pi-sparkles mr-1" style={{ fontSize: '0.7rem' }} />
                {t('Draft ready')}
            </span>
        ) : null;

    const automationBodyTemplate = (rowData) => {
        const mode = rowData?.automationState?.mode;
        if (!mode) return null;
        const presentation = getAutomationPresentation(mode);
        return <Tag value={t(presentation.label)} severity={presentation.severity} />;
    };

    const machineBodyTemplate = (rowData) => {
        const secondaryId =
            rowData?.machineSerialNumber ||
            rowData?.machineDeviceId ||
            rowData?.machineExternalSystemId;

        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold">
                    {rowData?.machineSearchDisplay || '-'}
                </span>
                {secondaryId && secondaryId !== rowData?.machineSearchDisplay ? (
                    <small className="text-color-secondary">{secondaryId}</small>
                ) : null}
            </div>
        );
    };

    const normalizedFilter = normalizeSearchValue(globalFilter).toLowerCase();
    const normalizedStartDate = startOfDay(startDate);
    const normalizedEndDate = endOfDay(endDate);
    const normalizedStatusFilter = normalizeSearchValue(statusFilter).toLowerCase();
    const normalizedAutomationFilter = normalizeSearchValue(automationFilter).toLowerCase();
    const statusOptions = [
        { label: t('All Statuses'), value: null },
        { label: t('Open'), value: 'open' },
        { label: t('Finance'), value: 'in_progress' },
        { label: t('Awaiting Customer'), value: 'awaiting_customer' },
        { label: t('Pending'), value: 'pending' }
    ];
    const filteredComplaintList = complaintList.filter((complaint) => {
        const matchesSearch = normalizedFilter
            ? complaint?.searchableText?.includes(normalizedFilter)
            : true;

        if (!matchesSearch) return false;
        if (normalizedStatusFilter && normalizeSearchValue(complaint?.status).toLowerCase() !== normalizedStatusFilter) return false;
        if (normalizedAutomationFilter && normalizeSearchValue(complaint?.automationState?.mode).toLowerCase() !== normalizedAutomationFilter) return false;
        if (activeView === 'sent') {
            const handoff = getLatestTechnicalHandoff(complaint);
            const currentTechnician = handoff?.repairOrder?.technician || handoff?.technician;
            if (technicianFilter && String(currentTechnician?._id || currentTechnician || '') !== technicianFilter) return false;
            if (technicalStageFilter === 'closed' && complaint?.status !== 'close') return false;
            if (technicalStageFilter === 'returned' && !handoff?.returnedAt) return false;
            if (technicalStageFilter === 'assigned' && (handoff?.returnedAt || complaint?.status === 'close' || !currentTechnician)) return false;
            if (technicalStageFilter === 'unassigned' && (handoff?.returnedAt || complaint?.status === 'close' || currentTechnician)) return false;
        }
        if (!normalizedStartDate && !normalizedEndDate) return true;

        const filterDateValue = activeView === 'sent'
            ? getLatestTechnicalHandoff(complaint)?.sentAt
            : complaint?.createdAt;
        const createdAt = filterDateValue ? new Date(filterDateValue) : null;
        if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

        if (normalizedStartDate && createdAt < normalizedStartDate) return false;
        if (normalizedEndDate && createdAt > normalizedEndDate) return false;

        return true;
    })
        // Preserve the API's newest-first order, including when filtering by reply status.
        .filter((complaint) => (onlyNeedsReply ? Boolean(complaint?._signals?.needsAgentReply) : true));

    const sortableComplaintList = filteredComplaintList.map((complaint) =>
        withOpenComplaintSortFields(complaint)
    );
    const needsReplyCount = complaintList.filter((c) => c?._signals?.needsAgentReply).length;
    const latestHandoff = getLatestTechnicalHandoff;
    const userName = (value) =>
        [value?.firstName, value?.lastName].filter(Boolean).join(' ') || value?.email || '-';
    const handoffDateBody = (rowData) => {
        const value = latestHandoff(rowData)?.sentAt;
        return value ? new Date(value).toLocaleString('es-PA') : '-';
    };
    const sentByBody = (rowData) => userName(latestHandoff(rowData)?.sentBy);
    const handoffReasonBody = (rowData) => latestHandoff(rowData)?.reason || '-';
    const closedDateBody = (rowData) =>
        rowData?.closedAt ? new Date(rowData.closedAt).toLocaleString('es-PA') : '-';
    const technicianBody = (rowData) => {
        const handoff = latestHandoff(rowData);
        return handoff?.returnedAt ? '-' : userName(handoff?.repairOrder?.technician || handoff?.technician);
    };
    const technicalStatusBody = (rowData) => {
        const handoff = latestHandoff(rowData);
        if (rowData?.status === 'close') return <Tag value="Cerrado" severity="success" />;
        if (handoff?.returnedAt) return <Tag value="Devuelto a Customer Support" severity="info" />;
        if (!handoff?.repairOrder?.technician && !handoff?.technician) {
            return <Tag value="Sin asignar" severity="warning" />;
        }
        return <Tag value="En atención técnica" severity="info" />;
    };
    const activeFilterCount = [
        startDate,
        endDate,
        statusFilter,
        automationFilter,
        technicalStageFilter,
        technicianFilter,
        onlyNeedsReply ? 'reply' : null
    ].filter(Boolean).length;
    const clearListFilters = () => {
        updateListQuery({
            from: null,
            to: null,
            ticketStatus: null,
            status: null,
            cashReturn: null,
            reply: null,
            automation: null,
            techStage: null,
            technician: null
        });
    };
    return (
        <div className="p-4">
            <ConfirmDialog />
            <p className="text-600">{activeView === 'sent' ? 'Historial de envíos: las fechas filtran el envío a técnicos.' : 'Pendientes actuales. Las fechas filtran la creación del ticket, no su estado al cierre del mes.'} Se excluyen conversaciones provisionales y abandonadas.</p>
            {activeView !== 'sent' && <p className="text-600">Fecha de creación: {startDate ? formatFilterDate(startDate) : 'Sin límite inicial'} — {endDate ? formatFilterDate(endDate) : 'Sin límite final'}</p>}
            <ComplaintScopeFilters id="open" origin={origin} onOriginChange={value => updateListQuery({ origin: value })} teamId={selectedTeam} onTeamChange={value => updateListQuery({ teamId: value })} lockedTeamId={activeView !== 'sent' && adminRoleUid === 'support' ? user?.data?.adminUser?.teamId : ''} />
            <div className="flex gap-3 align-items-center mb-3"><span>{includeInternal ? 'Incluye tickets técnicos internos del portal.' : 'Excluye tickets técnicos internos del portal.'}</span><Button label={includeInternal ? 'Excluir internos' : 'Incluir internos'} className="p-button-text p-button-sm" onClick={() => updateListQuery({ includeInternal: includeInternal ? null : '1' })} /></div>
            <div className="mb-3">
                <div className="flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <h3 className="m-0">{t('Open Complaints')}</h3>
                    <div className="flex gap-2 align-items-center flex-wrap">
                    {isAutomationAdmin && automationSettings ? (() => {
                        const status = getAdminAutomationStatus(automationSettings);
                        const cannotEnable = !status.configuredEnabled && !automationSettings.environmentEnabled;
                        return (
                            <div className="flex align-items-center gap-2 border-1 surface-border border-round px-3 py-2">
                                <Tag value={t(status.label)} severity={status.severity} />
                                <Button
                                    type="button"
                                    icon={status.configuredEnabled ? 'pi pi-power-off' : 'pi pi-play'}
                                    className={status.configuredEnabled ? 'p-button-sm p-button-danger p-button-text' : 'p-button-sm p-button-info p-button-text'}
                                    loading={automationToggleLoading}
                                    disabled={automationToggleLoading || cannotEnable}
                                    tooltip={cannotEnable ? t('Server enablement is required before the agent can be activated.') : t(status.configuredEnabled ? 'Turn off automation immediately.' : 'Shadow mode evaluates eligible tickets without sending customer messages.')}
                                    tooltipOptions={{ position: 'bottom' }}
                                    onClick={requestAutomationToggle}
                                />
                            </div>
                        );
                    })() : null}
                    <Button
                        type="button"
                        icon="pi pi-plus"
                        label="Crear reclamo manual"
                        className="p-button-sm"
                        onClick={() => setShowManualModal(true)}
                    />
                    </div>
                </div>

                <div className="flex justify-content-between align-items-center gap-3 flex-wrap border-top-1 border-bottom-1 surface-border py-2">
                    <div className="flex gap-1 align-items-center">
                        <Button
                            label="Pendientes de soporte"
                            icon="pi pi-inbox"
                            className={activeView === 'pending' ? 'p-button-sm p-button-text font-semibold' : 'p-button-sm p-button-text text-color-secondary'}
                            onClick={() => setActiveView('pending')}
                        />
                        <Button
                            label="Enviados a Técnicos"
                            icon="pi pi-history"
                            className={activeView === 'sent' ? 'p-button-sm p-button-text font-semibold' : 'p-button-sm p-button-text text-color-secondary'}
                            onClick={() => setActiveView('sent')}
                        />
                    </div>
                    <div className="flex gap-2 align-items-center flex-wrap justify-content-end">
                    <span className="p-input-icon-left w-full" style={{ maxWidth: '300px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilter}
                            onChange={(e) => updateListQuery({ q: e.target.value })}
                            placeholder={t('Search by machine, VM-467, serial, phone, location...')}
                            className="w-full"
                        />
                    </span>
                    <Button
                        type="button"
                        label={activeFilterCount ? `Filtros · ${activeFilterCount}` : 'Filtros'}
                        icon="pi pi-sliders-h"
                        className={activeFilterCount ? 'p-button-sm p-button-outlined' : 'p-button-sm p-button-text text-color-secondary'}
                        onClick={(event) => filterPanelRef.current?.toggle(event)}
                    />
                    {globalFilter ? (
                        <Button
                            type="button"
                            icon="pi pi-times"
                            className="p-button-sm p-button-text p-button-secondary"
                            tooltip="Limpiar búsqueda"
                            onClick={() => updateListQuery({ q: null })}
                        />
                    ) : null}
                    </div>
                </div>
            </div>

            <OverlayPanel ref={filterPanelRef} style={{ width: '28rem', maxWidth: 'calc(100vw - 2rem)' }}>
                <div className="flex justify-content-between align-items-center mb-3">
                    <span className="font-semibold">Filtrar reclamos</span>
                    {activeFilterCount ? (
                        <Button label="Limpiar" className="p-button-sm p-button-text" onClick={clearListFilters} />
                    ) : null}
                </div>
                <div className="flex flex-column gap-3">
                    {activeView === 'sent' ? (
                        <div>
                            <label className="block text-sm text-color-secondary mb-1">Historial</label>
                            <Dropdown
                                value={historyScope}
                                options={[
                                    { label: 'Enviados por mí', value: 'mine' },
                                    { label: 'Todo Customer Support', value: 'all' }
                                ]}
                                onChange={(e) => setHistoryScope(e.value)}
                                className="w-full"
                            />
                        </div>
                    ) : null}
                    <div>
                        <label className="block text-sm text-color-secondary mb-1">Estado del reclamo</label>
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(e) => updateListQuery({ ticketStatus: e.value, status: null })}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-color-secondary mb-1">Respuesta pendiente</label>
                        <Dropdown
                            value={onlyNeedsReply ? 'needs_reply' : null}
                            options={[
                                { label: 'Todos los reclamos', value: null },
                                { label: `Necesitan respuesta (${needsReplyCount})`, value: 'needs_reply' }
                            ]}
                            onChange={(e) => updateListQuery({ reply: e.value ? '1' : null })}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-color-secondary mb-1">Automatización</label>
                        <Dropdown
                            value={automationFilter}
                            options={[
                                { label: t('All automation states'), value: null },
                                { label: t('Automatic'), value: 'automatic' },
                                { label: t('Human review'), value: 'human_review' },
                                { label: t('Human owned'), value: 'human_owned' },
                                { label: t('Automation off'), value: 'off' }
                            ]}
                            onChange={(e) => updateListQuery({ automation: e.value })}
                            className="w-full"
                        />
                    </div>
                    {activeView === 'sent' ? (
                        <>
                            <div>
                                <label className="block text-sm text-color-secondary mb-1">Etapa técnica</label>
                                <Dropdown
                                    value={technicalStageFilter}
                                    options={[
                                        { label: 'Todas las etapas', value: null },
                                        { label: 'Devuelto a Customer Support', value: 'returned' },
                                        { label: 'Sin asignar', value: 'unassigned' },
                                        { label: 'En atención técnica', value: 'assigned' },
                                        { label: 'Cerrado', value: 'closed' }
                                    ]}
                                    onChange={(e) => updateListQuery({ techStage: e.value })}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-color-secondary mb-1">Técnico</label>
                                <Dropdown
                                    value={technicianFilter}
                                    options={technicianOptions}
                                    onChange={(e) => updateListQuery({ technician: e.value })}
                                    placeholder="Todos los técnicos"
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>
                        </>
                    ) : null}
                    <div>
                        <label className="block text-sm text-color-secondary mb-1">
                            {activeView === 'sent' ? 'Fecha de envío' : 'Fecha de creación'}
                        </label>
                        <div className="grid">
                            <div className="col-6">
                                <Calendar
                                    value={startDate}
                                    onChange={(e) => updateListQuery({ from: formatFilterDate(e.value) })}
                                    dateFormat="yy-mm-dd"
                                    placeholder="Desde"
                                    showIcon
                                    className="w-full"
                                />
                            </div>
                            <div className="col-6">
                                <Calendar
                                    value={endDate}
                                    onChange={(e) => updateListQuery({ to: formatFilterDate(e.value) })}
                                    dateFormat="yy-mm-dd"
                                    placeholder="Hasta"
                                    showIcon
                                    minDate={startDate || undefined}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </OverlayPanel>

            <DataTable
                paginator
                first={tableFirst}
                rows={tableRows}
                onPage={(event) =>
                    updateListQuery(
                        { first: event.first, rows: event.rows },
                        { resetPage: false }
                    )
                }
                rowsPerPageOptions={[5, 10, 15, 20]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                emptyMessage={t('No List found.')}
                responsiveLayout="scroll"
                value={sortableComplaintList}
                className="p-datatable-sm datatable-responsive"
                rowClassName={() => 'cursor-pointer'}
                onRowClick={(e) => handleNavigateToChat(e.data)}
            >
                <Column 
                    header={t('Machine')}
                    body={machineBodyTemplate}
                    field="machineSearchDisplay"
                />
                <Column header={t('Location')} field="machineLocation" />
                <Column header={t('WhatsApp Number')} field="whatsAppNumber" />
                <Column header={t('Date')} body={createdDateAndTimeBodyTemplate} />
                <Column header={t('Age')} body={ageBodyTemplate} field="ageSortMinutes" sortable />
                <Column header={t('Waiting')} body={waitingBodyTemplate} field="waitingSortMinutes" sortable />
                <Column header={t('Status')} body={statusBody} />
                <Column header={t('Automation')} body={automationBodyTemplate} />
                <Column body={originBodyTemplate} style={{ width: '7rem' }} />
                <Column body={draftBodyTemplate} style={{ width: '7rem' }} />
                {activeView === 'sent' ? <Column header="Enviado el" body={handoffDateBody} /> : null}
                {activeView === 'sent' ? <Column header="Enviado por" body={sentByBody} /> : null}
                {activeView === 'sent' ? <Column header="Motivo" body={handoffReasonBody} /> : null}
                {activeView === 'sent' ? <Column header="Técnico" body={technicianBody} /> : null}
                {activeView === 'sent' ? <Column header="Etapa técnica" body={technicalStatusBody} /> : null}
                {activeView === 'sent' ? <Column header="Cerrado el" body={closedDateBody} /> : null}
                <Column header={t('Chat')} body={chatBodyTemplate} />
            </DataTable>

            <Dialog
                header="Crear reclamo manual"
                visible={showManualModal}
                style={{ width: '42rem', maxWidth: '95vw' }}
                onHide={() => {
                    setShowManualModal(false);
                    resetManualForm();
                }}
                modal
            >
                <div className="grid">
                    <div className="col-12">
                        <label className="block mb-2">Máquina *</label>
                        <Dropdown
                            value={manualForm.machineId}
                            options={machineOptions}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, machineId: e.value }))}
                            placeholder="Seleccionar máquina"
                            className="w-full"
                            filter
                        />
                        {manualForm.machineId && (
                            <div className="mt-2" aria-live="polite">
                                <strong>Tipo de máquina: </strong>
                                {machineOptions.find((machine) => machine.value === manualForm.machineId)?.typeLabel || 'Sin tipo registrado'}
                            </div>
                        )}
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">Detalle del reclamo *</label>
                        <InputTextarea
                            value={manualForm.issueDetails}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, issueDetails: e.target.value }))}
                            rows={4}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">Tipo de reclamo *</label>
                        <Dropdown
                            value={manualForm.complaintType}
                            options={[
                                { label: 'Reembolso', value: 'refund' },
                                { label: 'Técnico', value: 'technical' },
                                { label: 'Otro', value: 'other' }
                            ]}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, complaintType: e.value }))}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">Canal de contacto</label>
                        <Dropdown
                            value={manualForm.contactChannel}
                            options={[
                                { label: 'WhatsApp', value: 'whatsapp' },
                                { label: 'Llamada telefónica', value: 'phone_call' }
                            ]}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, contactChannel: e.value }))}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <PhotoUploader
                            label="Foto del problema"
                            value={manualForm.photoUrl}
                            onChange={(val) => setManualForm((prev) => ({ ...prev, photoUrl: val }))}
                            helperText="Opcional. Carga una foto relacionada con el problema."
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">Nombre del cliente</label>
                        <InputText
                            value={manualForm.customerName}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, customerName: e.target.value }))}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">Teléfono del cliente</label>
                        <InputText
                            value={manualForm.customerPhone}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, customerPhone: e.target.value }))}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">Notas</label>
                        <InputTextarea
                            value={manualForm.manualNotes}
                            onChange={(e) => setManualForm((prev) => ({ ...prev, manualNotes: e.target.value }))}
                            rows={3}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 flex justify-content-end gap-2">
                        <Button
                            type="button"
                            label="Cancelar"
                            className="p-button-text"
                            onClick={() => {
                                setShowManualModal(false);
                                resetManualForm();
                            }}
                        />
                        <Button
                            type="button"
                            label={savingManual ? 'Guardando...' : 'Crear'}
                            disabled={savingManual || !manualForm.machineId || !manualForm.issueDetails.trim()}
                            onClick={submitManualComplaint}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default OpenComplaints;
